// src/api/roles.js
import axios from 'axios';
import { getAuthToken } from '../utils/storage';
import { API_BASE_URL } from './apiClient';

/**
 * Get list of roles with pagination
 * @param {Object} params - Query parameters { skip, limit }
 * @returns {Promise<Array>} List of roles
 */
/** Keep in sync with `role_router.list_roles` Query(..., le=…). */
const ROLES_LIST_MAX_LIMIT = 200;

export const getRolesList = async (params = {}) => {
    const token = getAuthToken();
    const skip = params.skip ?? 0;
    const limit = Math.min(
        Math.max(Number(params.limit ?? 20) || 20, 1),
        ROLES_LIST_MAX_LIMIT
    );
    try {
        const response = await axios.get(`${API_BASE_URL}/roles/get-list`, {
            params: {
                skip,
                limit,
                tenant_id: params.tenant_id,
            },
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching roles list:', error);
        throw error;
    }
};

/**
 * Get role details by ID
 * @param {number} roleId - Role ID
 * @returns {Promise<Object>} Role details
 */
export const getRole = async (roleId) => {
    const token = getAuthToken();
    try {
        const response = await axios.get(`${API_BASE_URL}/roles/get-role/${roleId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error(`Error fetching role ${roleId}:`, error);
        throw error;
    }
};

/**
 * Create a new role
 * @param {Object} roleData - Role data including name, description, permissions
 * @returns {Promise<Object>} Created role data
 */
export const createRole = async (roleData, tenantId = null) => {
    const token = getAuthToken();
    try {
        const params = tenantId != null ? { tenant_id: tenantId } : {};
        const response = await axios.post(`${API_BASE_URL}/roles/create-role`, roleData, {
            params,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating role:', error);
        throw error;
    }
};

/**
 * Delete a role
 * @param {number} roleId - Role ID
 * @returns {Promise<Object>} Response data
 */
export const deleteRole = async (roleId) => {
    const token = getAuthToken();
    try {
        const response = await axios.delete(`${API_BASE_URL}/roles/${roleId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error(`Error deleting role ${roleId}:`, error);
        throw error;
    }
};

/**
 * Update a role's description and permissions
 * @param {number} roleId - Role ID
 * @param {Object} updateData - { description, permissions }
 * @returns {Promise<Object>} Updated role data
 */
export const updateRole = async (roleId, updateData) => {
    const token = getAuthToken();
    try {
        const response = await axios.patch(
            `${API_BASE_URL}/roles/${roleId}`,
            {
                desc: updateData.description,
                permissions: updateData.permissions
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error(`Error updating role ${roleId}:`, error);
        throw error;
    }
};

const roleApi = {
    getRolesList,
    getRole,
    createRole,
    deleteRole,
    updateRole
};

export default roleApi; 